import React, { useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { Box, Typography } from '@mui/material'
import { Button } from 'src/components/atoms'
import CenteringLoading from 'src/components/molecules/CenteringLoading'
import EditCalculationMethod from 'src/components/molecules/EditCalculationMethod'
import OCRFormatTable, {
  OCRFormatTableProps,
} from 'src/components/molecules/OCRFormatTable'
import { Option } from 'src/components/molecules/SelectWithOption'
import SmallGuide from 'src/components/molecules/SmallGuide'
import {
  AdminAdminSalesReportManagerEditorSalesReportManagerEditorStoreManagerInOperationCenterRoles,
  isOneOfRoles,
} from 'src/domain/role'
import tabContentStyle from 'src/pages/TenantDetail/tabContentStyle'
import {
  CalculationMethod,
  CalculationRoundingTypeEnum,
  RoleEnum,
} from 'src/slices/services/api'
import { formatDateWithTimeAndWeekday } from 'src/utils/date'

export type OCRFormatTabProps = {
  role?: RoleEnum
  calculationMethod?: CalculationMethod
  roundingTypeOptions: Option[]
  precisionOptions: Option[]
  lastUpdatedAt?: string
  isLoading: boolean
  onClickEditButton: () => void
  onClickEditCalculationMethod: (
    roundingType: CalculationRoundingTypeEnum,
    precision: number
  ) => void
} & Partial<Pick<OCRFormatTableProps, 'className' | 'columns' | 'records'>>

const OCRFormatTab: React.StyledFC<OCRFormatTabProps> = ({
  className,
  isLoading,
  calculationMethod,
  roundingTypeOptions,
  precisionOptions,
  role,
  lastUpdatedAt,
  onClickEditButton,
  onClickEditCalculationMethod,
  columns = [],
  records = [],
}: OCRFormatTabProps) => {
  const tabClasses = tabContentStyle()
  const isViewer = role === RoleEnum.Viewer

  const showSwitchByRole = useCallback((): boolean => {
    return isOneOfRoles(
      AdminAdminSalesReportManagerEditorSalesReportManagerEditorStoreManagerInOperationCenterRoles,
      role
    )
  }, [role])

  const formattedLastUpdatedAt = useMemo(() => {
    return lastUpdatedAt ? formatDateWithTimeAndWeekday(lastUpdatedAt) : ''
  }, [lastUpdatedAt])

  const renderContent = (data: OCRFormatTableProps['records']) => {
    return data.length > 0 ? (
      <>
        <Box
          className={clsx(className, tabClasses.calcMethodWithLastUpdatedAt)}
        >
          <EditCalculationMethod
            calculationMethod={calculationMethod}
            roundingTypeOptions={roundingTypeOptions}
            precisionOptions={precisionOptions}
            onClickEditCalculationMethod={onClickEditCalculationMethod}
          />
          {lastUpdatedAt && (
            <Box className={tabClasses.lastUpdatedAt}>
              <Typography variant="subtitle2">最終更新日時</Typography>
              <Typography>{formattedLastUpdatedAt.toLocaleString()}</Typography>
            </Box>
          )}
        </Box>
        <OCRFormatTable
          className={tabClasses.table}
          columns={columns}
          records={data}
        />
        {showSwitchByRole() && (
          <Button
            className={tabClasses.editButton}
            title="対応表を編集"
            color="primary"
            size="large"
            onClick={onClickEditButton}
          />
        )}
      </>
    ) : (
      <SmallGuide
        icon="pray"
        message="利用準備がされていません"
        description="まずは登録して利用開始の準備をしましょう" // TODO: view の場合のテキストへの変更
        buttonTitle={isViewer ? '' : '設定'}
        onClick={onClickEditButton}
      />
    )
  }

  return (
    <Box
      className={clsx(
        tabClasses.root,
        records.length === 0 && tabClasses.guideRoot
      )}
    >
      {isLoading ? <CenteringLoading /> : renderContent(records)}
    </Box>
  )
}

export default OCRFormatTab
